import {
  Add,
  Button,
  Form,
  FormCard,
  FormItem,
  PageHeader,
  TextArea,
} from '@lmc/ui-kit'
import { useTranslation } from 'react-i18next'
import { ButtonsContainer, PageWrapper, SectionHeader } from '../components'
import { Form as AntdForm } from 'antd'
import { useRouter } from 'hooks/useRouter'
import useResident from 'hooks/useResident'
import {
  HomeDto,
  Resident,
  ResidentBody,
  ResidentCarePackageDTO,
} from '@lmc/ts-common'
import { LegacyRef, RefObject, useRef, useState } from 'react'
import { handleOnFinishFailed } from '../formComponents/utils'
import { useSelector } from 'react-redux'
import { DefaultOptionType } from 'antd/es/select'
import { FundingFormItem } from './components/FundingFormItem'
import NavigationModal from 'components/NavigationModal/NavigationModal'
import useFeatureFlags from 'hooks/useFeatureFlags'
import { scrollToSection } from 'screens/Residents/screens/ResidentProfileScreen/utils/utils'
import { getNominative } from 'common/utils'
import useCheckFundingType from '../hooks/useCheckFundingType'
import DeleteFundingTypeModal from '../ChangeManagement/DeleteFundingTypeModal'
import useHomeType from 'hooks/useHomeType'

export const EditCarePackageAndPatternsScreen = () => {
  const { t } = useTranslation()
  const [form] = AntdForm.useForm()
  const { navigate, params } = useRouter()
  const [isDirty, setIsDirty] = useState(false)
  const { resident, updateResident, isLoadingUpdate } = useResident({
    id: params?.id,
    onUpdateSuccess: () => navigate(-1),
  })
  const flags = useFeatureFlags({
    flagNames: ['rostering_change_management_part_2'],
  })?.data
  const { hasRota } = useHomeType()

  const ref = useRef()

  const flagsRosteringChangeManagementPart2Enabled =
    flags?.rostering_change_management_part_2?.enabled

  const home = useSelector(
    (state: { global: { home: HomeDto } }) => state.global.home
  )

  const residentNominative = getNominative(
    home.preferredPeopleNominative,
    'Resident'
  )

  const fundingTypeOptions: DefaultOptionType[] =
    home.rotaSettings?.fundingTypes?.map((fundingType) => ({
      label: fundingType.name,
      value: fundingType.id,
    })) || []

  const residentWithPackages = resident as unknown as Resident

  const [showDeleteFundingTypeModal, setShowDeleteFundingTypeModal] =
    useState(false)

  const { checkFundingTypeUsedInRota } = useCheckFundingType()

  const handleFormValuesChange = (values: ResidentCarePackageDTO) => {
    if (values) {
      setIsDirty(true)
    }
  }

  const handleOnUpdateFinish = (values: ResidentCarePackageDTO) => {
    setIsDirty(false)
    updateResident({
      carePackage: {
        ...values,
        fundingSources: values?.fundingSources?.map((source) => ({
          ...source,
        })),
      },
    } as unknown as ResidentBody)
  }

  const initialValues: ResidentCarePackageDTO =
    residentWithPackages?.carePackage || {}

  return (
    <>
      <PageHeader
        title={`${t('EDIT')} ${t('CARE_PACKAGE_AND_PATTERNS')}`}
        sticky
      >
        <ButtonsContainer>
          <Button onClick={() => navigate(-1)} data-cy="cancel-btn-rota">
            {t('CANCEL')}
          </Button>
          <Button
            variant="primary"
            onClick={form.submit}
            isLoading={isLoadingUpdate}
            data-cy="save-btn-rota"
          >
            {t('SAVE')}
          </Button>
        </ButtonsContainer>
      </PageHeader>
      <PageWrapper>
        <SectionHeader title={t('CARE_PACKAGE_AND_PATTERNS')} />
        <Form
          form={form}
          onFinish={handleOnUpdateFinish}
          initialValues={initialValues}
          onFinishFailed={({ errorFields }) =>
            handleOnFinishFailed(errorFields)
          }
          onValuesChange={handleFormValuesChange}
        >
          <AntdForm.List name="fundingSources">
            {(fields, { add, remove }) => (
              <FormCard
                title={t('FUNDING')}
                rightChildren={
                  <Button
                    variant="text"
                    type="button"
                    onClick={() => {
                      add()
                      scrollToSection(ref as unknown as RefObject<HTMLElement>)
                    }}
                    data-cy="add-funding-btn"
                  >
                    <Add />
                    {t('ADD_FUNDING')}
                  </Button>
                }
              >
                {fields.map((field, index) => {
                  const fundingTypeId = form.getFieldValue([
                    'fundingSources',
                    field.name,
                    'type',
                  ])

                  const handleDelete = async () => {
                    if (hasRota && flagsRosteringChangeManagementPart2Enabled) {
                      const addressUsed = await checkFundingTypeUsedInRota({
                        fundingTypeId,
                        residentId: resident?.id || '',
                      })

                      const areAllArraysEmpty =
                        addressUsed?.instances?.length === 0 &&
                        addressUsed?.patterns?.length === 0 &&
                        addressUsed?.revisions?.length === 0

                      if (areAllArraysEmpty) remove(index)
                      else setShowDeleteFundingTypeModal(true)
                    } else remove(index)
                  }

                  return (
                    <div ref={ref as unknown as LegacyRef<HTMLDivElement>}>
                      <FundingFormItem
                        key={field.name}
                        onDelete={handleDelete}
                        name={field?.name}
                        fundingTypeOptions={fundingTypeOptions}
                        selectedResident={resident?.id}
                      />
                    </div>
                  )
                })}
              </FormCard>
            )}
          </AntdForm.List>
          <FormCard title={t('ADDITIONAL_NOTES')}>
            <FormItem name="additionalNotes">
              <TextArea
                autoSize={{
                  minRows: 3,
                }}
                maxLength={200}
                showCount
                data-cy="additional-notes-input"
              />
            </FormItem>
          </FormCard>
          {hasRota && (
            <>
              <SectionHeader title={t('OTHER')} />
              <FormCard
                title={t('VISIT_PREFERENCES')}
                subtitle={t('VISIT_PREFERENCES_DESC', {
                  nominative: residentNominative,
                })}
              >
                <FormItem name="visitNotes">
                  <TextArea
                    autoSize={{
                      minRows: 3,
                    }}
                    maxLength={200}
                    showCount
                    data-cy="visit-notes-input"
                  />
                </FormItem>
              </FormCard>
            </>
          )}
        </Form>
      </PageWrapper>
      <NavigationModal
        canNavigate={!isDirty}
        modalContentText={t('CARE_PACKAGE_NAVIGATION_MODAL_CONTENT')}
      />
      {showDeleteFundingTypeModal && (
        <DeleteFundingTypeModal
          showModal={showDeleteFundingTypeModal}
          setShowModal={setShowDeleteFundingTypeModal}
        />
      )}
    </>
  )
}

export default EditCarePackageAndPatternsScreen
